import { ReactElement, useState } from "react";

import { useLocation, useNavigate } from "react-router";

import { FindingAgents } from "@/components/pages_flexible/match/specialty/FindingAgents";
import { Industries } from "@/components/pages_flexible/match/specialty/Industries";
import { JobFunctions } from "@/components/pages_flexible/match/specialty/JobFunctions";
import { JobTitles } from "@/components/pages_flexible/match/specialty/JobTitles";
import { MultipleChoice } from "@/components/pages_flexible/match/specialty/MultipleChoice";
import { SingleChoice } from "@/components/pages_flexible/match/specialty/SingleChoice";
import {
  FindMyAgentQuery,
  IntakeFormInput,
  MatchingCategoriesQuery,
  MatchingCategorySelectionType,
  useMatchingCategoriesQuery,
} from "@/graphql";
import { IntakeForm as ProfileIntakeForm } from "@/types/users";
import { ensureNonNullArray } from "@/utils/array";
import { NonNullableArrayItem } from "@/utils/types";

type MatchingCategory = NonNullableArrayItem<
  MatchingCategoriesQuery["matchingCategories"]
>;

type IntakeForm =
  | NonNullable<
      NonNullable<FindMyAgentQuery["optionalCurrentUser"]>["intakeForm"]
    >
  | ProfileIntakeForm;

export interface MatchStep {
  category?: MatchingCategory;
  hash: string;
  render: MatchingCategoryRenderFunction;
}

type MatchingCategoryRenderFunction = (props: {
  loading: boolean;
  intakeForm: IntakeForm;
  saveIntakeForm: (form?: IntakeFormInput) => void;
  category?: MatchingCategory;
  specialtySlug?: string;
}) => ReactElement;

const STEP_RENDER_FUNCTION_BY_SELECTION_TYPE: Record<
  MatchingCategorySelectionType,
  MatchingCategoryRenderFunction
> = {
  [MatchingCategorySelectionType.MultipleChoice]: ({
    loading,
    intakeForm,
    saveIntakeForm,
    category,
  }) => {
    const userMatchingOptionIds =
      intakeForm?.matchingOptions.map((o) => o.id) || [];
    const allMatchingOptionIdsInCategory = new Set(
      category?.matchingOptions.map((o) => o.id),
    );
    return (
      <MultipleChoice
        key={category?.id}
        loading={loading}
        initialSelectedIds={userMatchingOptionIds.filter((id) =>
          allMatchingOptionIdsInCategory.has(id),
        )}
        description={category?.description || ""}
        options={ensureNonNullArray(category?.matchingOptions)}
        onSubmit={(selectedIds) => {
          saveIntakeForm({
            matchingOptionIds: selectedIds,
            matchingCategoryId: category?.id || "",
          });
        }}
      />
    );
  },
  [MatchingCategorySelectionType.SingleChoice]: ({
    loading,
    saveIntakeForm,
    category,
  }) => {
    return (
      <SingleChoice
        key={category?.id}
        loading={loading}
        description={category?.description || ""}
        options={category?.matchingOptions || []}
        onSubmit={(selectedIds) => {
          if (!selectedIds.length) return;
          saveIntakeForm({
            matchingOptionIds: selectedIds,
            matchingCategoryId: category?.id || "",
          });
        }}
      />
    );
  },
  [MatchingCategorySelectionType.Industries]: ({
    loading,
    intakeForm,
    saveIntakeForm,
  }) => (
    <Industries
      loading={loading}
      intakeForm={intakeForm}
      saveIntakeForm={saveIntakeForm}
    />
  ),
  [MatchingCategorySelectionType.JobTitles]: ({
    loading,
    intakeForm,
    saveIntakeForm,
  }) => (
    <JobTitles
      loading={loading}
      intakeForm={intakeForm}
      saveIntakeForm={saveIntakeForm}
    />
  ),
  [MatchingCategorySelectionType.JobFunctions]: ({
    loading,
    intakeForm,
    saveIntakeForm,
  }) => (
    <JobFunctions
      loading={loading}
      intakeForm={intakeForm}
      saveIntakeForm={saveIntakeForm}
    />
  ),
};

export const FINDING_STEP_HASH = "FINDING";

const buildMatchSteps = (matchingCategories: MatchingCategory[]) => {
  const matchSteps = matchingCategories.map<MatchStep>((category) => {
    const hash = category?.name?.replaceAll(" ", "_").toUpperCase() || "";
    return {
      category,
      hash,
      render: STEP_RENDER_FUNCTION_BY_SELECTION_TYPE[category.selectionType],
    };
  });

  matchSteps.push({
    hash: FINDING_STEP_HASH,
    render: ({ intakeForm, saveIntakeForm, specialtySlug }) => (
      <FindingAgents
        intakeForm={intakeForm}
        saveIntakeForm={saveIntakeForm}
        specialtySlug={specialtySlug}
      />
    ),
  });
  return matchSteps;
};

export const useMatchSteps = (specialtySlug?: string) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<MatchStep>();
  const [matchSteps, setMatchSteps] = useState<MatchStep[]>([]);
  const location = useLocation();

  useMatchingCategoriesQuery({
    variables: { specialtySlug: specialtySlug || "" },
    onCompleted: (data) => {
      const categories = ensureNonNullArray(data.matchingCategories);
      const steps = buildMatchSteps(categories);
      setMatchSteps(steps);
      goToStep(steps[0], true);
    },
  });

  const stepIndex = step ? matchSteps.indexOf(step) : -1;

  function goToStep(stepObj: MatchStep, skipHistory = false) {
    if (!stepObj) return;

    setStep(stepObj);

    // when the step changes, make sure the URL contains
    // a hash so the browser history works correctly
    if (!skipHistory) {
      navigate({
        hash: `#${stepObj.hash}`,
        search: location.search,
      });
    }
  }

  function goNextStep() {
    goToStep(matchSteps[stepIndex + 1]);
  }

  function goBackStep() {
    goToStep(matchSteps[stepIndex - 1]);
  }

  return {
    step,
    stepIndex,
    matchSteps,
    goToStep,
    goNextStep,
    goBackStep,
  };
};
